import { useState, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { Button, Group, Popover, Stack, Text, type ButtonProps } from '@mantine/core';

/** Plain `ButtonProps` (Mantine's styling/variant props) doesn't include native
 * `<button>` DOM attributes like `title` — those only get merged in at the
 * polymorphic `Button` component's own call-site type, not on the interface
 * ConfirmButton extends here. This picks up the DOM attributes ConfirmButton's
 * call sites actually pass through (e.g. UsersPanel's `title` on the disabled
 * "remove yourself" case) without re-declaring Mantine's own props. */
type NativeButtonProps = Omit<ComponentPropsWithoutRef<'button'>, keyof ButtonProps | 'children' | 'onClick' | 'color'>;

/** Every reverse/void/cancel/delete action in the app posts an irreversible-
 * from-the-UI ledger effect (reversing entries, not edits — CLAUDE.md's
 * "Stock ledger is append-only"). Before this component, every one of those
 * buttons fired on a single click with zero confirmation (audit finding). No
 * new dependency is added — `@mantine/modals` is deliberately not installed;
 * this uses a plain `Popover` anchored to the trigger button instead of a
 * centered `Modal`, which keeps the "what am I about to break" message
 * physically next to the button the operator just pressed.
 *
 * Visually a drop-in replacement for the `Button` it guards: every `Button`
 * prop (`color`, `variant`, `size`, `leftSection`, ...) passes through to the
 * trigger unchanged via `...rest`. The only behavioural difference is that
 * `onConfirm` fires on the popover's own "Confirm" click, never on the first
 * click of the trigger. */
export interface ConfirmButtonProps extends Omit<ButtonProps, 'children' | 'onClick'>, NativeButtonProps {
  /** Visible text on the trigger button (mirrors the `Button` it replaces — this
   * component takes `label`, not `children`, so the pinned contract stays explicit). */
  label: ReactNode;
  /** One-line, plain-English consequence message shown in the confirm popover.
   * Should name the document and the effect, e.g. "Reverse this goods receipt?
   * Stock and cost will be backed out by a reversing ledger entry." */
  message: ReactNode;
  /** Fires only when the user clicks the popover's Confirm button — never on the
   * trigger's own click. May be sync or async: an async callback is awaited —
   * the popover stays open with the Confirm button in a loading state until it
   * settles (a second Confirm click while pending is ignored), then closes.
   * A rejection is logged via console.error as a dev-visible backstop and the
   * popover still closes cleanly — user-facing error display stays the caller's
   * job (every current site renders its own mutation error state, exactly as it
   * did around the plain `Button` this replaces). */
  onConfirm: () => void | Promise<void>;
  /** Text on the popover's confirming button. @default 'Confirm' */
  confirmLabel?: string;
  /** Text on the popover's cancelling button. @default 'Cancel' */
  cancelLabel?: string;
}

export function ConfirmButton({
  label,
  message,
  onConfirm,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  disabled,
  loading,
  ...rest
}: ConfirmButtonProps) {
  const [opened, setOpened] = useState(false);
  // True while an async onConfirm is in flight — the popover is held open with
  // the Confirm button in its loading state so a slow mutation is never
  // invisible, and a repeat Confirm click can't double-fire the action.
  const [pending, setPending] = useState(false);

  async function handleConfirm() {
    if (pending) return;
    setPending(true);
    try {
      await onConfirm();
    } catch (err) {
      // Dev-visible backstop only: every call site's mutation already surfaces
      // its own isError/error text next to the button — mirroring how the plain
      // Buttons this component replaced behaved.
      console.error('ConfirmButton: onConfirm rejected', err);
    } finally {
      setPending(false);
      setOpened(false);
    }
  }

  return (
    <Popover
      opened={opened}
      // While pending, refuse outside-click/escape close requests — the popover
      // is the only pending indication the user has.
      onChange={(next) => {
        if (!pending) setOpened(next);
      }}
      withArrow
      shadow="md"
      position="bottom"
      trapFocus
      // No animation delay: keeps the dropdown's mount synchronous with the
      // `opened` state change (also avoids relying on requestAnimationFrame,
      // which jsdom doesn't schedule the way real browsers do — component tests
      // click the trigger and assert on the dropdown in the same tick).
      transitionProps={{ duration: 0 }}
    >
      <Popover.Target>
        <Button {...rest} disabled={disabled} loading={loading} onClick={() => setOpened((o) => !o)}>
          {label}
        </Button>
      </Popover.Target>
      <Popover.Dropdown maw={280}>
        <Stack gap="xs">
          <Text size="sm">{message}</Text>
          <Group justify="flex-end" gap="xs">
            <Button size="xs" variant="default" disabled={pending} onClick={() => setOpened(false)}>
              {cancelLabel}
            </Button>
            <Button size="xs" color={rest.color ?? 'red'} loading={pending} onClick={() => void handleConfirm()}>
              {confirmLabel}
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
